import { EU_COUNTRIES, GenericCombobox, InputGroup } from '@/common'
import { orderBy } from 'es-toolkit'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

type AddressFieldIds =
  | 'street'
  | 'streetNumber'
  | 'poBox'
  | 'city'
  | 'subDivision'
  | 'street2'
  | 'postCode'
  | 'country'

export default function OrganizationAddressForm({
  initialState,
  onChange,
  fieldErrors
}: {
  initialState: {
    street: string
    streetNumber: string
    poBox: string
    city: string
    subDivision: string
    street2: string
    postCode: string
    country: string
  }
  onChange: (field: { [key: string]: string }) => void
  fieldErrors?: Partial<Record<AddressFieldIds, string>>
}) {
  const { t } = useTranslation()

  const { street, streetNumber, subDivision, postCode, poBox, city, street2 } = initialState

  const onInputChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
      onChange({ [evt.target.id]: evt.target.value })
    },
    [onChange]
  )

  const countryOptions = useMemo(
    () =>
      orderBy(
        EU_COUNTRIES.map(value => ({
          label: t(`countriesByIsoAlpha2.${value}`),
          value
        })),
        [option => option.label.toLowerCase().normalize('NFKD')],
        ['asc']
      ),
    [t]
  )

  const selectedCountry = useMemo(
    () => countryOptions.find(option => option.value === initialState.country) || null,
    [countryOptions, initialState.country]
  )

  return (
    <div className="grid grid-cols-6 gap-4">
      <div className="col-span-3">
        <InputGroup
          id="street"
          label={t('settings:organizationDetails.organizationAddress.fields.street')}
          value={street}
          onChange={onInputChange}
          error={fieldErrors?.street}
        />
      </div>

      <div className="col-span-3">
        <InputGroup
          id="streetNumber"
          label={t('settings:organizationDetails.organizationAddress.fields.streetNumber')}
          value={streetNumber}
          onChange={onInputChange}
          error={fieldErrors?.streetNumber}
        />
      </div>
      <div className="col-span-6">
        <InputGroup
          id="street2"
          label={t('settings:organizationDetails.organizationAddress.fields.street2')}
          value={street2}
          onChange={onInputChange}
        />
      </div>

      <div className="col-span-2">
        <InputGroup
          id="postCode"
          label={t('settings:organizationDetails.organizationAddress.fields.postCode')}
          value={postCode}
          onChange={onInputChange}
          error={fieldErrors?.postCode}
        />
      </div>
      <div className="col-span-2">
        <InputGroup
          id="city"
          label={t('settings:organizationDetails.organizationAddress.fields.city')}
          value={city}
          onChange={onInputChange}
          error={fieldErrors?.city}
        />
      </div>
      <div className="col-span-2">
        <InputGroup
          id="poBox"
          label={t('settings:organizationDetails.organizationAddress.fields.poBox')}
          value={poBox}
          onChange={onInputChange}
        />
      </div>
      <div className="col-span-3">
        <InputGroup
          id="subDivision"
          label={t('settings:organizationDetails.organizationAddress.fields.subDivision')}
          value={subDivision}
          onChange={onInputChange}
        />
      </div>
      <div className="col-span-3">
        <GenericCombobox
          label={t('settings:organizationDetails.organizationAddress.fields.country')}
          value={selectedCountry || null}
          onChange={evt => onChange({ country: evt?.value || '' })}
          items={countryOptions || []}
          error={fieldErrors?.country}
        />
      </div>
    </div>
  )
}
